import React, { useState, useEffect } from 'react';
import Button from '../customButtons/Button';
import AddProduct from './AddProduct';
import Modal from '../modal/Modal';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;

// Utility function to calculate days remaining
const calculateDaysRemaining = (dateValue) => {
    if (!dateValue || isNaN(new Date(dateValue))) {
        return null;
    }

    const now = new Date();
    const expiry = new Date(dateValue);

    now.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);

    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
};

// Utility function to format currency
const formatKshs = (amount) => {
    if (typeof amount !== 'number') return 'Kshs. Negotiable';
    return `Kshs. ${amount.toLocaleString('en-KE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`;
};

// Simple Alert Modal Component
const AlertModal = ({ isOpen, onClose, title, message, type = 'info' }) => {
    if (!isOpen) return null;

    const getIcon = () => {
        switch (type) {
            case 'success':
                return '✅';
            case 'error':
                return '❌';
            case 'warning':
                return '⚠️';
            default:
                return 'ℹ️';
        }
    };

    const getBgColor = () => {
        switch (type) {
            case 'success':
                return 'bg-teal-50 border-teal-200';
            case 'error':
                return 'bg-red-50 border-red-200';
            case 'warning':
                return 'bg-orange-50 border-orange-200';
            default:
                return 'bg-blue-50 border-blue-200';
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className={`bg-white rounded-lg shadow-xl max-w-sm w-full border ${getBgColor()}`}>
                <div className="p-6 text-center">
                    <div className="text-2xl mb-3">{getIcon()}</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{message}</p>
                    <Button
                        onClick={onClose}
                        variant="primary"
                        className="w-full"
                    >
                        OK
                    </Button>
                </div>
            </div>
        </div>
    );
};

// Confirmation Modal Component
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirm", cancelText = "Cancel" }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-sm w-full border border-gray-200">
                <div className="p-6 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                    <p className="text-gray-600 text-sm mb-6">{message}</p>
                    <div className="flex gap-3">
                        <Button
                            onClick={onClose}
                            variant="outline"
                            className="flex-1"
                        >
                            {cancelText}
                        </Button>
                        <Button
                            onClick={onConfirm}
                            variant="primary"
                            className="flex-1"
                        >
                            {confirmText}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// CORRECTED: MatchNotification Component with Stock Update
const MatchNotification = ({ match, productId, onMatchUpdate, token, product }) => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
    const [showRejectConfirm, setShowRejectConfirm] = useState(false);
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    const showAlert = (title, message, type = 'info') => {
        setAlertModal({ isOpen: true, title, message, type });
    };

    // CORRECTED: Check if order quantity exceeds available stock
    const isOrderExceedingStock = () => {
        const orderQuantity = parseFloat(match.quantity) || 0;
        const availableStock = parseFloat(product.quantity) || 0;
        return orderQuantity > availableStock;
    };

    const handleAccept = async () => {
        setShowAcceptConfirm(false);

        // Check if order exceeds available stock
        if (isOrderExceedingStock()) {
            showAlert(
                'Insufficient Stock',
                `Cannot accept order. Order quantity (${match.quantity} ${match.unit}) exceeds available stock (${product.quantity} ${product.unit}).`,
                'error'
            );
            return;
        }

        setLoading(true);
        try {
            // CORRECTED: First update the product stock
            const updatedQuantity = parseFloat(product.quantity) - parseFloat(match.quantity);

            const stockUpdateResponse = await fetch(
                `${API_URL}/api/products/${productId}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        quantity: updatedQuantity
                    })
                }
            );

            if (!stockUpdateResponse.ok) {
                throw new Error('Failed to update product stock');
            }

            // Then accept the match
            const matchResponse = await fetch(
                `${API_URL}/api/products/${productId}/matches/${match._id}/accept`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!matchResponse.ok) throw new Error('Failed to accept match');

            const acceptedMatch = await matchResponse.json();

            showAlert(
                'Order Accepted ✅',
                `Order accepted! Stock updated from ${product.quantity} to ${updatedQuantity} ${product.unit}. Buyer will be notified about delivery.`,
                'success'
            );

            // Redirect to match page with product and match details
            navigate('/match', {
                state: {
                    product: { ...product, quantity: updatedQuantity }, // Updated product with new quantity
                    match: acceptedMatch,
                    acceptedMatchId: match._id
                }
            });

            onMatchUpdate();
        } catch (err) {
            console.error('Error accepting match:', err);
            showAlert('Accept Failed', `❌ Failed to accept order: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        setShowRejectConfirm(false);
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/products/${productId}/matches/${match._id}/reject`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (!response.ok) throw new Error('Failed to reject match');

            await response.json();
            showAlert('Order Rejected', 'Order rejected.', 'info');
            onMatchUpdate();
        } catch (err) {
            console.error('Error rejecting match:', err);
            showAlert('Reject Failed', `Failed to reject order: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="bg-teal-50 border border-teal-200 p-3 sm:p-4 rounded-lg mb-2 last:mb-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-teal-700 mb-1 flex items-center flex-wrap gap-1">
                            <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                            </svg>
                            New Order Request! 🚚
                            {match.status === 'pending' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-normal whitespace-nowrap">
                                    PENDING CONFIRMATION
                                </span>
                            )}
                        </p>
                        <p className="text-xs text-teal-600 mb-2">
                            <span className="font-bold">{match.userName}</span> ordered{' '}
                            <span className="font-bold">{match.quantity} {match.unit}</span> for{' '}
                            <span className="font-bold text-teal-700">{formatKshs(match.price)}</span>.
                        </p>

                        {/* CORRECTED: Show stock availability warning */}
                        {isOrderExceedingStock() && (
                            <p className="text-xs text-red-600 font-semibold mt-1">
                                ⚠️ Order exceeds available stock ({product.quantity} {product.unit})
                            </p>
                        )}

                        {match.deliveryRequest && (
                            <p className="text-xs text-gray-600 mt-1">
                                📍 Delivery to: {match.deliveryRequest.deliveryAddress}
                            </p>
                        )}
                        <p className="text-xs text-gray-500">
                            Order placed: {new Date(match.createdAt).toLocaleDateString()}
                        </p>
                    </div>

                    {/* Action buttons */}
                    {match.status === 'pending' && (
                        <div className="flex sm:flex-col lg:flex-row gap-2 sm:gap-1 lg:gap-2 flex-shrink-0 sm:ml-4 sm:min-w-32">
                            <Button
                                onClick={() => setShowAcceptConfirm(true)}
                                variant="primary-small"
                                className="bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-2 min-w-20 flex-1 sm:flex"
                                disabled={loading || isOrderExceedingStock()}
                            >
                                {loading ? '...' : 'Accept ✅'}
                            </Button>
                            <Button
                                onClick={() => setShowRejectConfirm(true)}
                                variant="outline-small"
                                className="text-orange-600 border-orange-300 hover:bg-orange-50 text-sm px-3 py-2 min-w-20 flex-1 sm:flex-none"
                                disabled={loading}
                            >
                                {loading ? '...' : 'Reject ❌'}
                            </Button>
                        </div>
                    )}

                    {/* Status indicators */}
                    {match.status === 'accepted' && (
                        <span className="px-3 py-2 bg-teal-100 text-teal-800 text-xs font-semibold rounded-full sm:ml-4 whitespace-nowrap self-start sm:self-auto">
                            ✅ ACCEPTED
                        </span>
                    )}

                    {match.status === 'rejected' && (
                        <span className="px-3 py-2 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full sm:ml-4 whitespace-nowrap self-start sm:self-auto">
                            ❌ REJECTED
                        </span>
                    )}
                </div>
            </div>

            {/* Confirmation and Alert Modals */}
            <ConfirmationModal
                isOpen={showAcceptConfirm}
                onClose={() => setShowAcceptConfirm(false)}
                onConfirm={handleAccept}
                title="Accept Order"
                message={
                    isOrderExceedingStock()
                        ? `Cannot accept: Order quantity (${match.quantity} ${match.unit}) exceeds available stock (${product.quantity} ${match.unit}).`
                        : `Accept order from ${match.userName} for ${match.quantity} ${match.unit}? Stock will be updated from ${product.quantity} to ${parseFloat(product.quantity) - parseFloat(match.quantity)} ${match.unit}.`
                }
                confirmText={isOrderExceedingStock() ? "Stock Insufficient" : "Accept Order"}
                cancelText="Cancel"
            />

            <ConfirmationModal
                isOpen={showRejectConfirm}
                onClose={() => setShowRejectConfirm(false)}
                onConfirm={handleReject}
                title="Reject Order"
                message={`Reject order from ${match.userName}?`}
                confirmText="Reject Order"
            />

            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </>
    );
};

// --- CollapsibleMatches Component ---
const CollapsibleMatches = ({ matches, productId, onMatchUpdate, token, product }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Add null check for matches
    if (!matches || !Array.isArray(matches) || matches.length === 0) {
        return null;
    }

    // Always show the first match, and collapse the rest
    const firstMatch = matches[0];
    const remainingMatches = matches.slice(1);

    return (
        <div className="px-4 sm:px-5 pb-4">
            {/* Always show the first match */}
            <MatchNotification
                key={firstMatch._id}
                match={firstMatch}
                productId={productId}
                onMatchUpdate={onMatchUpdate}
                token={token}
                product={product}
            />

            {/* Show remaining matches in collapsible section */}
            {remainingMatches.length > 0 && (
                <div className="mt-2">
                    {/* Collapsible trigger */}
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center justify-between w-full text-xs text-teal-600 font-semibold hover:text-teal-800 transition-colors p-2 rounded-lg hover:bg-teal-50"
                    >
                        <span>
                            + {remainingMatches.length} more pending match{remainingMatches.length > 1 ? 'es' : ''}
                        </span>
                        <svg
                            className={`w-4 h-4 transform transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Collapsible content */}
                    {isExpanded && (
                        <div className="mt-2 space-y-2 animate-fadeIn">
                            {remainingMatches.map((match) => (
                                <MatchNotification
                                    key={match._id}
                                    match={match}
                                    productId={productId}
                                    onMatchUpdate={onMatchUpdate}
                                    token={token}
                                    product={product}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Action Menu Component ---
const ActionMenu = ({ product, onEdit, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleEditClick = () => {
        setIsOpen(false);
        onEdit(product);
    };

    const handleDeleteClick = () => {
        setIsOpen(false);
        onDelete(product);
    };

    return (
        <div className="">
            {/* Three dots button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Product actions"
            >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
            </button>

            {/* Dropdown menu */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
                    <div className="py-1">
                        <button
                            onClick={handleEditClick}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit Product
                        </button>
                        <button
                            onClick={handleDeleteClick}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete Product
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- Edit Product Modal Component ---
const EditProductModal = ({ product, isOpen, onClose, onSave }) => {
    const { token } = useAuth();
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        category: '',
        description: '',
        price: '',
        discountedPrice: '',
        quantity: '',
        unit: '',
        baseUnit: '',
        pricePerBaseUnit: '',
        discountedPricePerBaseUnit: '',
        minOrderQuantity: '',
        expiry: '',
        image: '',
        tags: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    // Product type options
    const productTypes = [
        { value: 'input', label: 'Agricultural Input' },
        { value: 'produce', label: 'Produce' }
    ];

    // Category options based on type
    const categoryOptions = {
        input: [
            'Fertiliser', 'Seedlings', 'Pesticide', 'Herbicide'
        ],
        produce: [
            'Fruits', 'Vegetables', 'Cereals', 'Legumes'
        ]
    };

    // Unit options
    const unitOptions = [
        'kg', 'g', 'ton', 'lbs', 'oz',
        'liter', 'ml', 'gallon',
        'bushels', 'bags', 'packets', 'sacks', 'bales',
        'units', 'pieces', 'crates', 'dozens', 'bunches'
    ];

    const showAlert = (title, message, type = 'info') => {
        setAlertModal({ isOpen: true, title, message, type });
    };

    useEffect(() => {
        if (product && isOpen) {
            setFormData({
                name: product.name || '',
                type: product.type || '',
                category: product.category || '',
                description: product.description || '',
                price: product.price || '',
                discountedPrice: product.discountedPrice || '',
                quantity: product.quantity || '',
                unit: product.unit || '',
                baseUnit: product.baseUnit || '',
                pricePerBaseUnit: product.pricePerBaseUnit || '',
                discountedPricePerBaseUnit: product.discountedPricePerBaseUnit || '',
                minOrderQuantity: product.minOrderQuantity || '',
                expiry: product.expiry ? new Date(product.expiry).toISOString().split('T')[0] : '',
                image: product.image || '',
                tags: Array.isArray(product.tags) ? product.tags.join(', ') : ''
            });
        }
    }, [product, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Auto-calculate base unit prices when quantity, price, or discounted price changes
        if (['quantity', 'price', 'discountedPrice'].includes(name) && formData.baseUnit) {
            calculateBaseUnitPrices(name, value);
        }
    };

    const calculateBaseUnitPrices = (changedField, value) => {
        const quantity = changedField === 'quantity' ? parseFloat(value) || 0 : parseFloat(formData.quantity) || 0;
        const price = changedField === 'price' ? parseFloat(value) || 0 : parseFloat(formData.price) || 0;
        const discountedPrice = changedField === 'discountedPrice' ? parseFloat(value) || 0 : parseFloat(formData.discountedPrice) || 0;

        if (quantity > 0) {
            setFormData(prev => ({
                ...prev,
                pricePerBaseUnit: (price / quantity).toFixed(2),
                discountedPricePerBaseUnit: (discountedPrice / quantity).toFixed(2)
            }));
        }
    };

    const handleBaseUnitChange = (e) => {
        const baseUnit = e.target.value;
        setFormData(prev => ({
            ...prev,
            baseUnit
        }));

        // Recalculate prices when base unit is selected
        if (formData.quantity > 0) {
            calculateBaseUnitPrices('baseUnit', baseUnit);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Prepare data for API - ensure all number fields are properly converted
            const submitData = {
                name: formData.name,
                type: formData.type,
                category: formData.category,
                description: formData.description,
                price: parseFloat(formData.price) || 0,
                discountedPrice: parseFloat(formData.discountedPrice) || 0,
                quantity: parseFloat(formData.quantity) || 0,
                unit: formData.unit,
                baseUnit: formData.baseUnit,
                pricePerBaseUnit: parseFloat(formData.pricePerBaseUnit) || 0,
                discountedPricePerBaseUnit: parseFloat(formData.discountedPricePerBaseUnit) || 0,
                minOrderQuantity: parseFloat(formData.minOrderQuantity) || 0,
                expiry: formData.expiry,
                image: formData.image,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
            };

            console.log('Submitting product update:', submitData); // Debug log

            const response = await fetch(`${API_URL}/api/products/${product._id}`, {
                method: 'PUT', // This matches your backend
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(submitData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to update product: ${response.status}`);
            }

            const updatedProduct = await response.json();
            console.log('Product updated successfully:', updatedProduct); // Debug log
            showAlert('Success', 'Product updated successfully!', 'success');
            onSave(updatedProduct);
            onClose();
        } catch (err) {
            console.error('Error updating product:', err);
            setError(err.message);
            showAlert('Error', `Failed to update product: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Edit Product</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                            aria-label="Close modal"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
                                {error}
                            </div>
                        )}

                        {/* Product Type and Category */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Product Type *
                                </label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                >
                                    <option value="">Select Type</option>
                                    {productTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Category *
                                </label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleChange}
                                    required
                                    disabled={!formData.type}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm disabled:bg-gray-100"
                                >
                                    <option value="">Select Category</option>
                                    {formData.type && categoryOptions[formData.type]?.map(category => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Product Name and Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Product Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                placeholder="Enter product name"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                placeholder="Describe your product..."
                            />
                        </div>

                        {/* Quantity and Units */}
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Quantity *
                                </label>
                                <input
                                    type="number"
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleChange}
                                    required
                                    min="0.01"
                                    step="0.01"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Unit *
                                </label>
                                <select
                                    name="unit"
                                    value={formData.unit}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                >
                                    <option value="">Select Unit</option>
                                    {unitOptions.map(unit => (
                                        <option key={unit} value={unit}>{unit}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Base Unit *
                                </label>
                                <select
                                    name="baseUnit"
                                    value={formData.baseUnit}
                                    onChange={handleBaseUnitChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                >
                                    <option value="">Select Base Unit</option>
                                    {unitOptions.map(unit => (
                                        <option key={unit} value={unit}>{unit}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Pricing */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Original Price (Kshs) *
                                </label>
                                <input
                                    type="number"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    step="0.01"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Discounted Price (Kshs) *
                                </label>
                                <input
                                    type="number"
                                    name="discountedPrice"
                                    value={formData.discountedPrice}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    step="0.01"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                            </div>
                        </div>

                        {/* Price per Base Unit (Read-only) */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Price per {formData.baseUnit || 'Base Unit'}
                                </label>
                                <input
                                    type="number"
                                    name="pricePerBaseUnit"
                                    value={formData.pricePerBaseUnit}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Discounted Price per {formData.baseUnit || 'Base Unit'}
                                </label>
                                <input
                                    type="number"
                                    name="discountedPricePerBaseUnit"
                                    value={formData.discountedPricePerBaseUnit}
                                    readOnly
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                                />
                            </div>
                        </div>

                        {/* Minimum Order and Expiry */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Minimum Order Quantity *
                                </label>
                                <input
                                    type="number"
                                    name="minOrderQuantity"
                                    value={formData.minOrderQuantity}
                                    onChange={handleChange}
                                    required
                                    min="0"
                                    step="0.01"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Expiry Date *
                                </label>
                                <input
                                    type="date"
                                    name="expiry"
                                    value={formData.expiry}
                                    onChange={handleChange}
                                    required
                                    min={new Date().toISOString().split('T')[0]}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                />
                            </div>
                        </div>

                        {/* Image and Tags */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Image URL
                            </label>
                            <input
                                type="url"
                                name="image"
                                value={formData.image}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                placeholder="https://example.com/image.jpg"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Tags
                            </label>
                            <input
                                type="text"
                                name="tags"
                                value={formData.tags}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                placeholder="organic, fresh, premium (separate with commas)"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Separate multiple tags with commas
                            </p>
                        </div>

                        {/* Current Image Preview */}
                        {formData.image && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Image Preview
                                </label>
                                <div className="w-32 h-32 border border-gray-300 rounded-md overflow-hidden">
                                    <img
                                        src={formData.image}
                                        alt="Product preview"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = 'https://placehold.co/128x128/f3f4f6/555555?text=Image+Error';
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>

            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </>
    );
};

// --- Delete Confirmation Modal Component ---
const DeleteConfirmationModal = ({ product, isOpen, onClose, onConfirm }) => {
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    const showAlert = (title, message, type = 'info') => {
        setAlertModal({ isOpen: true, title, message, type });
    };

    const handleDelete = async () => {
        setLoading(true);
        setError('');

        try {
            console.log('Deleting product:', product._id); // Debug log

            const response = await fetch(`${API_URL}/api/products/${product._id}`, {
                method: 'DELETE', // This matches your backend
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to delete product: ${response.status}`);
            }

            console.log('Product deleted successfully'); // Debug log
            showAlert('Success', 'Product deleted successfully!', 'success');
            onConfirm();
            onClose();
        } catch (err) {
            console.error('Error deleting product:', err);
            setError(err.message);
            showAlert('Error', `Failed to delete product: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Delete Product</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                            aria-label="Close modal"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
                                {error}
                            </div>
                        )}

                        <p className="text-gray-600 text-sm">
                            Are you sure you want to delete <strong>"{product.name}"</strong>?
                            This action cannot be undone and all associated matches will be lost.
                        </p>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={loading}
                            >
                                {loading ? 'Deleting...' : 'Delete Product'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </>
    );
};

// --- AllProducts Component ---
const AllProducts = () => {
    const { token, isAuthenticated, loading: authLoading } = useAuth();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });

    // State for action modals
    const [editingProduct, setEditingProduct] = useState(null);
    const [deletingProduct, setDeletingProduct] = useState(null);

    const showAlert = (title, message, type = 'info') => {
        setAlertModal({ isOpen: true, title, message, type });
    };

    const handleOpenAddProductModal = () => {
        setIsAddProductModalOpen(true);
    };

    const fetchProducts = async () => {
        if (!token) {
            setLoading(false);
            setError("Authentication required to view your listings.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/products/my-products`, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to load listings. Status: ${response.status}`);
            }

            let data = await response.json();

            // Ensure data is an array
            if (!Array.isArray(data)) {
                console.error('Expected array but got:', typeof data, data);
                data = [];
            }

            // Ensure all match status arrays are explicitly created and attached
            data = data.map((product) => {
                if (!product) return null;

                // Safely get all matches, defaulting to an empty array
                const allMatches = Array.isArray(product.matches) ? product.matches : [];

                // Filter matches by status
                const pendingMatches = allMatches.filter(m => m && m.status === 'pending');
                const acceptedMatches = allMatches.filter(m => m && m.status === 'accepted');
                const rejectedMatches = allMatches.filter(m => m && m.status === 'rejected');

                // Create simplified Match object for rendering
                const mapMatchData = (m) => ({
                    _id: m._id,
                    userName: m.buyer?.name || m.buyer?.email || 'Buyer',
                    quantity: m.quantity,
                    unit: m.unit,
                    price: m.offeredPrice,
                    status: m.status,
                    createdAt: m.createdAt,
                    // Only include delivery request for pending matches to simplify data structure
                    deliveryRequest: m.status === 'pending' && product.deliveryRequests?.find(
                        dr => dr && dr._id && m.deliveryRequestId && dr._id.toString() === m.deliveryRequestId.toString()
                    )
                });

                return {
                    ...product,
                    // Attach all required match arrays (which are now guaranteed to be arrays)
                    possibleMatches: allMatches.map(mapMatchData),
                    pendingMatches: pendingMatches.map(mapMatchData),
                    acceptedMatches: acceptedMatches.map(mapMatchData),
                    rejectedMatches: rejectedMatches.map(mapMatchData),

                    // Update status based on pending matches
                    status: pendingMatches.length > 0 ? 'matched' : product.status
                };
            }).filter(product => product !== null); // Remove any null products

            setProducts(data);
        } catch (err) {
            console.error("Failed to fetch products:", err.message);
            setError(`Failed to load your listings. Please try again later.`);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseAddProductModal = () => {
        setIsAddProductModalOpen(false);
        fetchProducts();
    };

    // CORRECTED: Update product quantity in local state after order acceptance
    const handleProductStockUpdate = (productId, newQuantity) => {
        setProducts(prev => prev.map(p =>
            p._id === productId ? { ...p, quantity: newQuantity } : p
        ));
    };

    // Action handlers
    const handleEditProduct = (product) => {
        setEditingProduct(product);
    };

    const handleDeleteProduct = (product) => {
        setDeletingProduct(product);
    };

    const handleSaveProduct = (updatedProduct) => {
        setProducts(prev => prev.map(p =>
            p._id === updatedProduct._id ? updatedProduct : p
        ));
        setEditingProduct(null);
    };

    const handleConfirmDelete = () => {
        setProducts(prev => prev.filter(p => p._id !== deletingProduct._id));
        setDeletingProduct(null);
    };

    // Auto-refresh matches every 30 seconds
    useEffect(() => {
        if (!token) return;

        fetchProducts(); // Initial load

        const interval = setInterval(() => {
            fetchProducts();
        }, 30000); // Check every 30 seconds

        return () => clearInterval(interval);
    }, [token, isAuthenticated, authLoading]);

    // Calculate total pending matches for badge with null check
    const totalPendingMatches = products.reduce((total, product) => {
        return total + (product && product.pendingMatches ? product.pendingMatches.length : 0);
    }, 0);

    // --- RENDER BLOCK ---
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center text-teal-600 text-lg sm:text-xl p-4">
                <svg className="animate-spin h-6 w-6 sm:h-8 sm:w-8 mr-3 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className='text-sm'>Loading your surplus listings...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-red-600 text-base sm:text-lg p-4 text-center">
                <p className="mb-4 font-bold">Error Loading Listings</p>
                <p className="mb-6 max-w-md">{error}</p>
                <Button onClick={fetchProducts} variant="outline">
                    Retry Fetching Listings
                </Button>
            </div>
        );
    }

    // Add null check for products
    if (!products || !Array.isArray(products)) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-gray-600 text-base sm:text-lg p-4 text-center">
                <p className="mb-4">Unable to load products</p>
                <Button onClick={fetchProducts} variant="outline">
                    Try Again
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Header with Match Badge */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-8">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className='flex flex-col space-y-2'>
                            <h1 className="text-xl sm:text-xl font-bold text-teal-600">
                                My Input Stock Listing
                            </h1>
                            <p className='text-[14px] text-gray-500'>Manage your stock level by tracking expiry remaining days, available match, and recommendations</p>
                        </div>
                        {totalPendingMatches > 0 && (
                            <span className="bg-orange-500 text-white text-xs font-medium px-3 py-1 rounded-full animate-pulse whitespace-nowrap">
                                {totalPendingMatches} Pending Match{totalPendingMatches > 1 ? 'es' : ''}
                            </span>
                        )}
                    </div>

                    {/* Show header button ONLY when there are products */}
                    {products.length > 0 && (
                        <Button
                            variant="primary"
                            className="flex items-center gap-2 whitespace-nowrap shrink-0 bg-teal-600 hover:bg-teal-700"
                            onClick={handleOpenAddProductModal}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            Add New Product
                        </Button>
                    )}
                </div>

                {products.length === 0 ? (
                    <div className="text-center border border-gray-100 rounded-xl shadow-sm py-16 px-6 max-w-4xl mx-auto">
                        {/* Illustrative Icon */}
                        <div className="mx-auto w-20 h-20 sm:w-24 sm:h-24 mb-6 text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-full h-full">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                            </svg>
                        </div>

                        {/* Headline and Body */}
                        <h2 className="text-xl sm:text-[15px] text-gray-900 mb-3">You are not tracking any stock</h2>
                        <p className="text-gray-500 mb-2 max-w-xl mx-auto text-sm sm:text-[14px]">
                            Your nearly expiry inventory is a hidden asset. List it now to find a match, prevent waste, and recover value. It only takes a minute to list your first item.
                        </p>
                        <p className="text-gray-500 text-xs sm:text-sm mb-8 max-w-md mx-auto">
                        </p>

                        {/* Primary Call-to-Action */}
                        <Button
                            onClick={handleOpenAddProductModal}
                            className="flex items-center text-white gap-2 bg-teal-600 hover:bg-teal-700 justify-center mx-auto mb-6 shadow-sm"
                            variant="primary"
                            size="medium"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                            List Your First Item
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {products.map((product) => {
                            // Comprehensive null check for product and its properties
                            if (!product) return null;

                            const daysRemaining = calculateDaysRemaining(product.expiry);

                            let expiryText;
                            let expiryColor;
                            let displayDate;

                            if (daysRemaining === null) {
                                expiryText = 'Date Pending';
                                expiryColor = 'text-orange-600 font-bold';
                                displayDate = 'N/A';
                            } else if (daysRemaining > 0) {
                                expiryText = `${daysRemaining} days left`;
                                expiryColor = daysRemaining <= 7 ? 'text-red-600' : 'text-gray-600';
                                displayDate = new Date(product.expiry).toLocaleDateString();
                            } else if (daysRemaining === 0) {
                                expiryText = 'Expires Today';
                                expiryColor = 'text-red-600 font-bold';
                                displayDate = new Date(product.expiry).toLocaleDateString();
                            } else {
                                expiryText = 'EXPIRED';
                                expiryColor = 'text-gray-400 line-through';
                                displayDate = new Date(product.expiry).toLocaleDateString();
                            }

                            const currentStatus = product.status;
                            const hasPendingMatches = product.pendingMatches && product.pendingMatches.length > 0;
                            const hasAcceptedMatches = product.acceptedMatches && product.acceptedMatches.length > 0;

                            let statusText;
                            let statusClasses;

                            if (hasPendingMatches) {
                                statusText = `${product.pendingMatches.length} PENDING`;
                                statusClasses = 'bg-orange-500 text-white animate-pulse';
                            } else if (hasAcceptedMatches) {
                                statusText = 'ACCEPTED';
                                statusClasses = 'bg-teal-500 text-white';
                            } else if (currentStatus === 'matched') {
                                statusText = 'MATCHED';
                                statusClasses = 'bg-teal-600 text-white';
                            } else if (currentStatus === 'good' || currentStatus === 'active') {
                                statusText = 'ACTIVE';
                                statusClasses = 'bg-teal-500 text-white';
                            } else {
                                statusText = 'AWAITING REVIEW';
                                statusClasses = 'bg-orange-500 text-white';
                            }

                            return (
                                <div key={product._id} className="bg-white border border-gray-200 rounded-md sm:w-full relative">
                                    {/* Action Menu */}
                                    <div className="absolute top-4 right-0 z-10">
                                        <ActionMenu
                                            product={product}
                                            onEdit={handleEditProduct}
                                            onDelete={handleDeleteProduct}
                                        />
                                    </div>

                                    <div className="flex flex-col sm:flex-row">
                                        {/* Left Side: Image and Details */}
                                        <div className="flex-grow  p-4 sm:p-5 flex items-start sm:items-center gap-4 min-w-0">
                                            <div className="flex-shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200">
                                                <img
                                                    className="h-full w-full object-cover"
                                                    src={product.image || 'https://placehold.co/64x64/f3f4f6/555555?text=Crop'}
                                                    alt={product.name}
                                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/64x64/f3f4f6/555555?text=Crop'; }}
                                                />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h3 className="text-sm sm:text-[14px] font-semibold text-gray-900 truncate mb-1">
                                                    {product.name || 'Unnamed Product'}
                                                </h3>
                                                <div className="flex items-center flex-wrap gap-2 text-xs sm:text-[12px] text-gray-500">
                                                    <span className="font-semibold text-gray-700">
                                                        {product.quantity || 0} {product.unit || 'units'}
                                                    </span>
                                                    <span className="text-gray-300 hidden sm:inline">|</span>
                                                    <span className={`${expiryColor} font-medium`}>
                                                        {expiryText}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Expires: {displayDate}
                                                </p>

                                                {/* Match Summary */}
                                                {product.possibleMatches && product.possibleMatches.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 text-xs">
                                                        {product.acceptedMatches && product.acceptedMatches.length > 0 && (
                                                            <span className="text-teal-600 font-semibold">
                                                                {product.acceptedMatches.length} accepted
                                                            </span>
                                                        )}
                                                        {product.rejectedMatches && product.rejectedMatches.length > 0 && (
                                                            <span className="text-gray-500">
                                                                {product.rejectedMatches.length} rejected
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right Side: Status and Price */}
                                        <div className="flex-shrink-0 sm:w-40 bg-gray-50 p-4 sm:p-5 border-t sm:border-t-0 sm:border-l border-gray-100 flex flex-row sm:flex-col justify-between sm:justify-center items-center sm:items-end text-center sm:text-right gap-2 sm:gap-0">
                                            {/* Price */}
                                            <p className="text-md font-bold text-teal-600 mb-0 sm:mb-2 leading-none">
                                                {formatKshs(product.discountedPrice)}
                                            </p>

                                            {/* Status Tag */}
                                            <span className={`px-3 py-1 text-[9px] font-semibold tracking-wider uppercase rounded-full ${statusClasses} whitespace-nowrap`}>
                                                {statusText}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Collapsible Matches Section */}
                                    {product.pendingMatches && product.pendingMatches.length > 0 && (
                                        <CollapsibleMatches
                                            matches={product.pendingMatches}
                                            productId={product._id}
                                            onMatchUpdate={fetchProducts}
                                            token={token}
                                            product={product}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Add Product Modal */}
            {isAddProductModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={handleCloseAddProductModal}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
                            aria-label="Close modal"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <AddProduct onClose={handleCloseAddProductModal} />
                    </div>
                </div>
            )}

            {/* Edit Product Modal */}
            <EditProductModal
                product={editingProduct}
                isOpen={!!editingProduct}
                onClose={() => setEditingProduct(null)}
                onSave={handleSaveProduct}
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                product={deletingProduct}
                isOpen={!!deletingProduct}
                onClose={() => setDeletingProduct(null)}
                onConfirm={handleConfirmDelete}
            />

            {/* Global Alert Modal */}
            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </div>
    );
};

export default AllProducts;